/* eslint-disable no-unused-vars */
import { useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader, DropDownWrapper, Each, ParagraphWithTooltip, MenuItem } from 'components';
import styled, { css } from 'styled-components';
import { Palette } from 'config';
import { campaignReportsRequestTypes, campaignReportsUniqueFields } from 'constants';
import { getQueryString, useRerender } from 'utils';
import { useParams } from 'react-router-dom';
import { useSignal, useComputed } from '@preact/signals-react';
import { campaignReportsAPI } from 'services/campaignReports.service';
import { createPortal } from 'react-dom';
import { AppSearchItem, CountrySearchItem, HeadSearchItem } from './DropDownItems';

const checkValue = (searchValue) => (appValue) =>
  appValue?.toString().toLowerCase().includes(searchValue.toLowerCase());

const searchItems = {
  Apps: AppSearchItem,
  Country: CountrySearchItem,
};

const fieldsForFiltering = {
  Apps: ['Apps', 'pba_number'],
  Country: ['Country', 'CountryCode'],
};

export const DropDown = ({
  dropDownIsOpen$,
  containerRef,
  searchValue$,
  dateFrom$,
  dateTo$,
  isActive,
  toggle,
  toggleAllOff,
  selectedAppsOrCountries$,
}) => {
  const showAppName = useSelector((state) => state.auth.show_app_name);
  const overlayContainer = useRef(document.createElement('div'));
  const { cmp_id } = useParams();
  const rerender = useRerender();
  const currentRequestType = useSelector((state) => state.campaignReports.tableRequestTypes);
  const timePeriod$ = useComputed(() => ({ from: dateFrom$.value, to: dateTo$.value }));
  const queryString = getQueryString(currentRequestType, timePeriod$.value, cmp_id);

  const appsList$ = useSignal(null);

  const { data, isSuccess, isLoading, isError } = campaignReportsAPI.useGetCampaignReportQuery(
    queryString,
    {
      selectFromResult: (response) => {
        appsList$.value = response?.data?.appsData;
        return { ...response, data: response?.data?.appsData };
      },
      skip: !(
        currentRequestType === campaignReportsRequestTypes.Applications ||
        currentRequestType === campaignReportsRequestTypes.Country
      ),
    },
  );

  let showLoader = isLoading || appsList$.value === undefined;

  const filteredApps$ = useComputed(() => {
    if (searchValue$.value.length < 2) return appsList$.value;
    return appsList$.value.filter((appData) => {
      const checkSearchValueFunc = checkValue(searchValue$.value);
      const filteringFieldsArr =
        currentRequestType === 'Apps' && !showAppName
          ? ['pba_number']
          : fieldsForFiltering[currentRequestType];
      return filteringFieldsArr.some((field) => checkSearchValueFunc(appData[field]));
    });
  });

  const isAllItemsSelected = !selectedAppsOrCountries$.value.size;

  useEffect(() => {
    document.body.appendChild(overlayContainer.current);
    return () => {
      document.body.removeChild(overlayContainer.current);
    };
  }, []);

  const getObjForAllBtn = useMemo(() => {
    if (currentRequestType === 'Apps')
      return { text: 'All Applications', value: 'all_applications' };
    if (currentRequestType === 'Country') return { text: 'All Countries', value: 'all_countries' };
    return { text: '', value: '' };
  }, [currentRequestType]);

  return (
    <>
      {dropDownIsOpen$.value &&
        createPortal(<Slice onClick={() => {}} />, overlayContainer.current)}
      <DropDownWrapper
        isOpen$={dropDownIsOpen$}
        width={340}
        targetRef={containerRef}
        showSlice={false}
        style={{ top: '60px', left: '5px' }}
        outSideClickAction={() => {}}
      >
        <ContentBox>
          {showLoader ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                padding: '6px',
              }}
            >
              <Loader.Local delay={0} />
            </div>
          ) : (
            <div style={{}}>
              {filteredApps$.value.length ? (
                <>
                  <HeadSearchItem
                    isAllItemsSelected={isAllItemsSelected}
                    getObjForAllBtn={getObjForAllBtn}
                    toggleAllOff={toggleAllOff}
                    rerender={rerender}
                  />
                  <Each
                    fieldOfIndex={campaignReportsUniqueFields[currentRequestType]}
                    of={filteredApps$.value}
                    render={(item, index) => {
                      const Component = searchItems[currentRequestType];
                      return (
                        <Component
                          isActive={isActive}
                          item={item}
                          index={index}
                          toggle={toggle}
                          rerender={rerender}
                          searchValue$={searchValue$}
                        />
                        //
                      );
                    }}
                  />
                </>
              ) : (
                <div style={{ textAlign: 'center' }}>No Apps.</div>
              )}
            </div>
          )}
        </ContentBox>
      </DropDownWrapper>
    </>
  );
};

const ContentBox = styled.div`
  overscroll-behavior: none;
  height: auto;
  max-height: 180px;
  overflow-y: auto;
  ::-webkit-scrollbar {
    width: 6px;
  }
  display: flex;
  justify-content: center;
  ::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 8px;
  }
`;

const Slice = styled.div`
  position: fixed;
  min-width: 100vw;
  min-height: 100vh;
  top: 0;
  left: 0;
  opacity: 0;
  z-index: 1500;
`;
